/**
 * compactDdlCard.facts — fact-line + object-name builders keyed off
 * `extractDdlStructure`'s `DdlStructure` union. Pure, reuse-only: every field
 * read here was already produced by the VDE structural extractor
 * (`sql-resolver/ddl/extractDdlStructure`) — this module never re-parses SQL,
 * it only formats already-parsed facts into the display strings the compact
 * DDL card shows (§2.9 S13, plan-9 decision 2).
 *
 * Split out of `compactDdlCard.ts` to keep that module focused on
 * classification + top-level dispatch.
 */
import type { AlterTableClause, DdlStructure } from '../../../sql-resolver/types';

import { vqeCopy } from '../ux/vqeCopy';

const facts = vqeCopy.ddlCard.facts;

function alterTableFacts(clauses: readonly AlterTableClause[]): readonly string[] {
  const lines: string[] = [];
  let opaqueCount = 0;
  for (const clause of clauses) {
    if (clause.kind === 'add-column') lines.push(facts.addsColumn(clause.column.name));
    else if (clause.kind === 'drop-column') lines.push(facts.dropsColumn(clause.columnName));
    else if (clause.kind === 'rename-column') lines.push(facts.renamesColumn(clause.oldName, clause.newName));
    else opaqueCount += 1;
  }
  if (opaqueCount > 0) lines.push(facts.otherChanges(opaqueCount));
  return lines;
}

/**
 * Fact lines for a successfully-extracted `DdlStructure`. Every
 * `DdlStructureKind` arm is handled explicitly; the arms extract-ddl-
 * structure never produces on its own (this module is only ever called with
 * its output) fall through the `default` defensively.
 */
export function factsFromStructure(structure: DdlStructure): readonly string[] {
  switch (structure.kind) {
    case 'create-table':
      return [facts.columns(structure.columns.length)];
    case 'alter-table':
      return alterTableFacts(structure.clauses);
    case 'create-index': {
      const lines = [facts.onTable(structure.index.table.raw)];
      if (structure.index.unique) lines.push(facts.uniqueIndex);
      lines.push(facts.indexColumns(structure.index.columns.length));
      return lines;
    }
    case 'drop-index':
      return structure.table ? [facts.onTable(structure.table.raw)] : [];
    case 'create-type':
    case 'create-domain': {
      const parsed = structure.parsed;
      if (!parsed) return [];
      if (parsed.enumValues && parsed.enumValues.length > 0) return [facts.enumValues(parsed.enumValues.length)];
      if (parsed.baseType) return [facts.baseType(parsed.baseType)];
      return [];
    }
    case 'create-trigger':
    case 'alter-trigger':
    case 'drop-trigger': {
      const parsed = structure.parsed;
      if (!parsed) return [];
      const lines: string[] = [];
      if (parsed.target) lines.push(facts.onTable(parsed.target.raw));
      if (parsed.timing && parsed.events && parsed.events.length > 0) {
        lines.push(facts.triggerFires(parsed.timing, parsed.events));
      }
      return lines;
    }
    case 'create-routine':
    case 'alter-routine':
    case 'drop-routine': {
      const parsed = structure.parsed;
      if (!parsed || parsed.params === undefined) return [];
      return [facts.parameters(parsed.params.length)];
    }
    case 'grant':
    case 'revoke': {
      const parsed = structure.parsed;
      if (!parsed) return [];
      return [facts.privileges(parsed.privileges.length, parsed.grantees.length)];
    }
    default:
      return [];
  }
}

/**
 * Display object name for a successfully-extracted `DdlStructure` — the
 * `raw` (verbatim-quoted) form of the parsed `QualifiedName`. Returns `null`
 * when the arm carries no single well-formed name (grant/revoke: the
 * "object" is a privilege target with a plain-string name, not a full
 * qualified identifier — decision 2 omits an object row there and relies on
 * the privilege/grantee fact line instead).
 */
export function objectNameFromStructure(structure: DdlStructure): string | null {
  switch (structure.kind) {
    case 'create-table':
    case 'alter-table':
      return structure.table.raw;
    case 'create-index':
      return structure.index.name.raw;
    case 'drop-index':
      return structure.indexName.raw;
    case 'create-type':
    case 'create-domain':
    case 'create-trigger':
    case 'alter-trigger':
    case 'drop-trigger':
    case 'create-routine':
    case 'alter-routine':
    case 'drop-routine':
      return structure.parsed?.name.raw ?? null;
    default:
      return null;
  }
}

/**
 * Table-Designer seed info — only `create-table` / `alter-table` carry a
 * resolvable table reference (plan-9 decision 3). `name` is unquoted (the
 * Designer tab wants the bare identifier); `schema` mirrors the parsed
 * qualifier when present.
 */
export function tableRefFromStructure(
  structure: DdlStructure,
): { readonly name: string; readonly schema?: string } | null {
  if (structure.kind === 'create-table' || structure.kind === 'alter-table') {
    return { name: structure.table.name, schema: structure.table.schema };
  }
  return null;
}
